// frontend/src/contexts/UserContext.tsx
/**
 * 🔐 USER CONTEXT - Estado global del usuario autenticado
 *
 * Propósito: Centralizar toda la información del usuario en un solo lugar,
 * evitando múltiples peticiones HTTP y re-renders innecesarios.
 *
 * Patrón: Singleton + Observer (React Context)
 * Performance: Memoización con useMemo para evitar re-renders en cascada
 */

import React, { createContext, useContext, useState, useEffect, useMemo, useCallback, ReactNode } from 'react';
import {
	ROUTE_AUTH_ME
} from '../config/routes/routeConfig';
import { fetchWithAuth } from '../shared/utils/helpers/fetchUtils';
import { logger } from '../shared/utils/logger';
import { Area, Language } from '../types/common.types';
import { UserPermissions } from '../types/user.types';

/**
 * 📦 Tipo de datos del contexto
 */
interface UserContextType {
	// Datos del usuario
	userId: number | null;
	area: Area | Area[] | null;
	permissions: UserPermissions | null;
	empresa: string | null;
	language: Language | null;

	// Estado de carga
	loading: boolean;
	error: string | null;

	// Acciones
	refetchUserData: () => Promise<void>;
	clearUserData: () => void;
}

/**
 * 🏗️ Crear contexto con valor por defecto undefined
 * Esto obliga a que se use dentro de un Provider
 */
const UserContext = createContext<UserContextType | undefined>(undefined);

interface UserProviderProps {
	children: ReactNode;
}

/**
 * 🎯 Provider del contexto de usuario
 *
 * Optimizaciones aplicadas:
 * - Peticiones en paralelo con Promise.all
 * - Memoización del value con useMemo
 * - useCallback para funciones estables
 * - Carga inicial solo una vez
 */
export const UserProvider: React.FC<UserProviderProps> = ({ children }) => {
	// Estados
	const [userId, setUserId] = useState<number | null>(null);
	const [area, setArea] = useState<Area | Area[] | null>(null);
	const [permissions, setPermissions] = useState<UserPermissions | null>(null);
	const [empresa, setEmpresa] = useState<string | null>(null);
	const [language, setLanguage] = useState<Language | null>(null);
	const [loading, setLoading] = useState<boolean>(true);
	const [error, setError] = useState<string | null>(null);

	/**
	 * 🚀 Cargar todos los datos del usuario con endpoint unificado (OPTIMIZADO)
	 * 1 petición HTTP en lugar de 5 = mucho más rápido
	 * useCallback para mantener referencia estable
	 */
	const fetchUserData = useCallback(async (): Promise<void> => {
		try {
			setLoading(true);
			setError(null);

			// 🚀 UNA SOLA petición al endpoint unificado
			const response = await fetchWithAuth(ROUTE_AUTH_ME, { method: 'GET' });

			if (!response.ok) {
				throw new Error(`Error al obtener perfil de usuario: ${response.status}`);
			}

			const data = await response.json();

			// ✅ Procesar datos del endpoint unificado
			// Backend devuelve: { userId, area, empresa, permissions, language }

			// UserId
			const userId = data.userId;
			setUserId(userId);

			// Area
			setArea(data.area);

			// Empresa (puede ser string o objeto con .nombre)
			const empresaNombre = typeof data.empresa === 'object' && data.empresa !== null
				? data.empresa.nombre
				: data.empresa;
			setEmpresa(empresaNombre);

			// Permissions
			setPermissions(data.permissions || {});

			// Language
			const lang = data.language || 'ES';
			setLanguage(lang);

		} catch (err) {
			const errorMessage = err instanceof Error ? err.message : 'Error al cargar datos del usuario';
			setError(errorMessage);
			logger.error('UserContext: Error en fetchUserData', err);
		} finally {
			setLoading(false);
		}
	}, []); // Sin dependencias = función estable

	/**
	 * 🗑️ Limpiar datos del usuario (útil para logout)
	 */
	const clearUserData = useCallback(() => {
		setUserId(null);
		setArea(null);
		setPermissions(null);
		setEmpresa(null);
		setLanguage(null);
		setError(null);
	}, []);

	// Carga inicial solo una vez
	useEffect(() => {
		fetchUserData();
	}, [fetchUserData]);

	/**
	 * 🎯 Memoizar el value para evitar re-renders innecesarios
	 * Solo se recalcula si cambian las dependencias
	 */
	const value = useMemo(
		() => ({
			userId,
			area,
			permissions,
			empresa,
			language,
			loading,
			error,
			refetchUserData: fetchUserData,
			clearUserData
		}),
		[userId, area, permissions, empresa, language, loading, error, fetchUserData, clearUserData]
	);

	return (
		<UserContext.Provider value={value}>
			{children}
		</UserContext.Provider>
	);
};

/**
 * 🪝 Hook personalizado para usar el contexto
 *
 * Uso:
 * ```tsx
 * const { userId, area, permissions, empresa, loading } = useUser();
 * ```
 *
 * @throws {Error} Si se usa fuera del UserProvider
 */
export const useUser = (): UserContextType => {
	const context = useContext(UserContext);
	if (!context) {
		throw new Error('useUser must be used within a UserProvider');
	}
	return context;
};

/**
 * 🎯 Hook para acceder solo al área (optimización)
 * Útil cuando solo necesitas el área y no quieres re-renders por otros cambios
 */
export const useUserArea = () => {
	const { area, loading } = useUser();
	return useMemo(() => ({ area, loading }), [area, loading]);
};

/**
 * 🎯 Hook para acceder solo a permisos (optimización)
 */
export const useUserPermissions = () => {
	const { permissions, loading } = useUser();
	return useMemo(() => ({ permissions, loading }), [permissions, loading]);
};

/**
 * 🎯 Hook para acceder solo a empresa (optimización)
 */
export const useUserCompany = () => {
	const { empresa, loading, error } = useUser();
	return useMemo(() => ({ empresa, loading, error }), [empresa, loading, error]);
};

/**
 * 🎯 Hook para acceder solo al ID (optimización)
 */
export const useUserId = () => {
	const { userId, loading, error } = useUser();
	return useMemo(() => ({ userId, loading, error }), [userId, loading, error]);
};

/**
 * 🎯 Hook para acceder solo al idioma (optimización)
 */
export const useUserLanguage = () => {
	const { language, loading, error } = useUser();
	return useMemo(() => ({ language, loading, error }), [language, loading, error]);
};
